import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { Tournament } from '../../models/tournaments/tournament.model';
import { TournamentsFiltersRequest } from '../../models/tournaments/requests/api-tournaments-get-request.model';
import { ApiTournamentsService } from '../../services/tournaments/api/api-tournaments.service';
import { AlertsService } from '../../services/alerts.service';
import { ModalsService } from '../../services/modals.service';
import { DateService } from '../../services/date.service';
import { TournamentType } from '../../enums/tournaments/tournament-type.enum';
import { TournamentAddModalComponent } from './modals/tournament-add-modal/tournament-add-modal.component';
import { TournamentEditModalComponent } from './modals/tournament-edit-modal/tournament-edit-modal.component';
import { TournamentUsersModalComponent } from './modals/tournament-users-modal/tournament-users-modal.component';
import { ConfigurationsBlockComponent } from '../configurations/configurations-block/configurations-block.component';
import { ConfigurationDef } from '../configurations/configurations-block/configuration-def.model';

@Component({
  templateUrl: 'tournaments.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, TooltipModule, CollapseModule, TabsModule, TournamentAddModalComponent, TournamentEditModalComponent, TournamentUsersModalComponent, ConfigurationsBlockComponent]
})
export class TournamentsComponent implements OnInit {

  @ViewChild('addModal') addModal!: TournamentAddModalComponent;
  @ViewChild('editModal') editModal!: TournamentEditModalComponent;
  @ViewChild('usersModal') usersModal!: TournamentUsersModalComponent;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  readonly tournamentConfigurations: ConfigurationDef[] = [
    { name: 'game_tournament_feature_active', label: 'Feature Active', type: 'bool' },
    { name: 'game_tournament_level_unlocked', label: 'Level Unlocked', type: 'number' },
    { name: 'game_tournament_match_count', label: 'Match Count', type: 'number' },
    { name: 'tournaments_clean_timeout', label: 'Clean Timeout', type: 'number' },
  ];

  tournaments: Tournament[] = [];
  isLoading: boolean = false;
  totalResults: number = 0;
  readonly resultsPerPage: number = 25;

  isFilterOpen: boolean = false;
  filters: TournamentsFiltersRequest = new TournamentsFiltersRequest();
  startDateMinFilter: string = '';
  startDateMaxFilter: string = '';

  private static pageNumber: number = 1;

  get pageNumber(): number { return TournamentsComponent.pageNumber; }
  get totalPages(): number { return Math.ceil(this.totalResults / this.resultsPerPage); }

  readonly tournamentTypes = Object.entries(TournamentType).filter(([, v]) => typeof v === 'number') as [string, number][];

  getPageNumbers(): (number | null)[] {
    const total = this.totalPages;
    const current = this.pageNumber;
    const visibleSet = new Set<number>();
    for (let i = 1; i <= Math.min(2, total); i++) visibleSet.add(i);
    for (let i = Math.max(total - 1, 1); i <= total; i++) visibleSet.add(i);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) visibleSet.add(i);
    const sorted = Array.from(visibleSet).sort((a, b) => a - b);
    const result: (number | null)[] = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
      result.push(sorted[i]);
    }
    return result;
  }

  goToPage(page: number): void { TournamentsComponent.pageNumber = page; this.refreshTournaments(); }
  onClickPaginationPrevious(): void { TournamentsComponent.pageNumber--; this.refreshTournaments(); }
  onClickPaginationNext(): void { TournamentsComponent.pageNumber++; this.refreshTournaments(); }

  constructor(
    public dateService: DateService,
    private apiTournamentsService: ApiTournamentsService,
    private alertsService: AlertsService,
    private modalsService: ModalsService
  ) {}

  ngOnInit(): void {
    this.refreshTournaments();
  }

  getTournamentTypeName(type: number): string {
    return TournamentType[type] ?? String(type);
  }

  getTournamentStatus(tournament: Tournament): 'waiting' | 'in_progress' | 'past' {
    const now = Math.floor(Date.now() / 1000);
    if (now < tournament.startDateTimestamp) return 'waiting';
    if (now <= tournament.endDateTimestamp) return 'in_progress';
    return 'past';
  }

  onClickFilter(): void {
    this.filters.start_date_min_timestamp = this.startDateMinFilter ? Math.floor(new Date(this.startDateMinFilter).getTime() / 1000) : null;
    this.filters.start_date_max_timestamp = this.startDateMaxFilter ? Math.floor(new Date(this.startDateMaxFilter).getTime() / 1000) : null;
    TournamentsComponent.pageNumber = 1;
    this.refreshTournaments();
  }

  onClickResetFilters(): void {
    this.filters = new TournamentsFiltersRequest();
    this.startDateMinFilter = '';
    this.startDateMaxFilter = '';
    TournamentsComponent.pageNumber = 1;
    this.refreshTournaments();
  }

  onClickAdd(): void {
    this.addModal.show(() => this.refreshTournaments());
  }

  onClickAddFromFile(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.isLoading = true;
    this.apiTournamentsService.addTournamentsFromFile(file).then(
      () => {
        this.alertsService.displayAlertSuccess('Tournaments imported successfully!');
        this.refreshTournaments();
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error importing tournaments: ' + error);
      }
    );
    input.value = '';
  }

  onClickEdit(tournament: Tournament): void {
    this.editModal.show(tournament, () => this.refreshTournaments());
  }

  onClickUsers(tournament: Tournament): void {
    this.usersModal.show(tournament.tournamentId);
  }

  onClickDelete(tournament: Tournament): void {
    this.modalsService.showConfirmModal(() => {
      this.apiTournamentsService.deleteTournament(tournament.tournamentId).then(
        () => {
          this.alertsService.displayAlertSuccess('Tournament deleted !');
          this.refreshTournaments();
        }, error => {
          this.alertsService.displayAlertError('Error deleting tournament : ' + error);
        }
      );
    });
  }

  private refreshTournaments(): void {
    this.isLoading = true;
    this.apiTournamentsService.getTournaments(this.pageNumber, this.resultsPerPage, this.filters).then(
      response => {
        this.isLoading = false;
        this.tournaments = response.results;
        this.totalResults = response.resultsTotal;
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error loading tournaments : ' + error);
      }
    );
  }
}
